import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { GridPaginationModel } from "@mui/x-data-grid";
import { Org } from "~/lib/types";
import {
  LoadOrganizationUsersDocument,
  GetUserProfileDocument,
  useGetUserProfileQuery,
  useSetOrganizationMembershipMutation,
  useSetSpaceMembershipMutation,
  useRemoveSpaceMembershipMutation,
  useRemoveOrganizationMembershipMutation,
} from "~/operations";
import { getError } from "~/lib/handle-error";
import { sortRolesBySeniority, stripOrgPrefix } from "../lib";
import { SpaceAccess, UserProfile } from "../types";

export type UseUserProfileOptions = {
  org: Org;
  userMrn: string | undefined;
};

const DEFAULT_SPACES_PAGE_SIZE = 10;

export function useUserProfile({ org, userMrn }: UseUserProfileOptions) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // State for dialogs
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
  const [isEditUsersDialogOpen, setIsEditUsersDialogOpen] = useState(false);
  const [isEditOrgRoleDialogOpen, setIsEditOrgRoleDialogOpen] = useState(false);
  const [editSpaceRoleDialog, setEditSpaceRoleDialog] = useState<{
    open: boolean;
    space: SpaceAccess | null;
  }>({ open: false, space: null });
  const [addSpaceDialog, setAddSpaceDialog] = useState<{
    open: boolean;
    space: { mrn: string; name: string } | null;
  }>({ open: false, space: null });

  // Search state for space access table
  const [spaceSearch, setSpaceSearch] = useState("");

  // Pagination state for spaces
  const [spacesPaginationModel, setSpacesPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: DEFAULT_SPACES_PAGE_SIZE,
    });

  // Cache cursors for each page
  const [cursorCache, setCursorCache] = useState<Map<number, string>>(
    new Map(),
  );

  // Calculate after cursor for current page
  const after =
    spacesPaginationModel.page > 0
      ? cursorCache.get(spacesPaginationModel.page - 1)
      : undefined;

  // Mutation hooks
  const [setOrganizationMembership, { loading: isEditRoleLoading }] =
    useSetOrganizationMembershipMutation();
  const [setSpaceMembership, { loading: isSaveSpaceRoleLoading }] =
    useSetSpaceMembershipMutation();
  const [removeSpaceMembership, { loading: isRemoveSpaceLoading }] =
    useRemoveSpaceMembershipMutation();
  const [removeOrganizationMembership, { loading: isRemoveUserLoading }] =
    useRemoveOrganizationMembershipMutation();

  // Fetch user profile data
  const { data, loading, error } = useGetUserProfileQuery({
    variables: {
      mrn: org.mrn,
      identitiesFilter: userMrn ? [userMrn] : undefined,
      includeChildrenOnly: true,
      first: spacesPaginationModel.pageSize,
      after,
    },
    skip: !userMrn || !org.mrn,
  });

  const spaceInfo = data?.spaceInfo?.members;
  const pageInfo = spaceInfo?.pageInfo;

  // Memoize rowCount to prevent MUI DataGrid from resetting pagination during loading
  const rowCountRef = useRef(0);
  const spacesRowCount = useMemo(() => {
    const newCount = spaceInfo?.totalCount;
    if (newCount !== undefined) {
      rowCountRef.current = newCount;
    }
    return rowCountRef.current;
  }, [spaceInfo?.totalCount]);

  // Cache endCursor for current page when data arrives
  useEffect(() => {
    if (pageInfo?.endCursor) {
      setCursorCache((prev) => {
        const next = new Map(prev);
        next.set(spacesPaginationModel.page, pageInfo.endCursor);
        return next;
      });
    }
  }, [pageInfo?.endCursor, spacesPaginationModel.page]);

  // Reset cursor cache and go to first page when search changes
  useEffect(() => {
    setCursorCache(new Map());
    setSpacesPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [spaceSearch]);

  // Transform API response to UserProfile type
  const user = useMemo((): UserProfile | null => {
    // Get user info from the userInfo query
    const userMember = data?.userInfo?.members?.edges?.[0]?.node;
    if (!userMember) return null;

    const identity = userMember.identity;
    // Only handle User identity type
    if (identity.__typename !== "User") return null;

    // Get space access from the spaceInfo query
    const spaceEdges = data?.spaceInfo?.members?.edges || [];
    const spaces: SpaceAccess[] = spaceEdges
      .filter((edge) => edge?.node?.resource)
      .map((edge) => {
        const node = edge!.node!;
        return {
          id: node.resource.mrn,
          mrn: node.resource.mrn,
          name: node.resource.name,
          description: "", // Not available in API
          assetCount: 0, // Not available in API
          members: [], // Not available in API
          lastActive: null, // Not available in API
          roles: sortRolesBySeniority(
            node.roles.map((role) => ({
              mrn: role.mrn,
              title: stripOrgPrefix(role.title),
            })),
          ),
        };
      });

    // Get the most senior organization role
    const sortedOrgRoles = sortRolesBySeniority(
      userMember.roles.map((role) => ({
        mrn: role.mrn,
        title: stripOrgPrefix(role.title),
      })),
    );

    return {
      mrn: identity.mrn,
      name: identity.name,
      email: identity.email || "",
      teamCount: 0, // Not available in API yet
      spaceCount: spacesRowCount,
      organizationRole: sortedOrgRoles[0] || { mrn: "", title: "No Role" },
      spaces,
    };
  }, [data, spacesRowCount]);

  // Note: Search filtering is now handled server-side via the query parameter
  // This will be implemented when the API supports it
  const filteredSpaces = user?.spaces || [];

  // Handle pagination model changes
  const handleSpacesPaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      // If page size changed, reset to first page and clear cache
      if (newModel.pageSize !== spacesPaginationModel.pageSize) {
        setCursorCache(new Map());
        setSpacesPaginationModel({ page: 0, pageSize: newModel.pageSize });
      } else {
        setSpacesPaginationModel(newModel);
      }
    },
    [spacesPaginationModel.pageSize],
  );

  // Handlers
  const handleBackToUsers = () => {
    navigate(`/organization/users?organizationId=${org.id}`);
  };

  const handleRemoveUser = () => {
    setIsRemoveDialogOpen(true);
  };

  const handleConfirmRemove = async () => {
    try {
      if (!userMrn) throw new Error("User MRN is required");
      if (!org.mrn) throw new Error("Organization MRN is required");

      await removeOrganizationMembership({
        variables: {
          input: {
            orgMrn: org.mrn,
            userMrn,
          },
        },
        refetchQueries: [LoadOrganizationUsersDocument],
      });

      enqueueSnackbar("User removed from organization successfully", {
        variant: "success",
      });
      setIsRemoveDialogOpen(false);
      handleBackToUsers();
    } catch (error) {
      const errorMessage = getError(error);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };

  const handleCancelRemove = () => {
    setIsRemoveDialogOpen(false);
  };

  const handleEditUsers = () => {
    setIsEditUsersDialogOpen(true);
  };

  const handleCancelEditUsers = () => {
    setIsEditUsersDialogOpen(false);
  };

  const handleSaveEditUsers = async (data: { removedSpaces: string[] }) => {
    try {
      if (!userMrn) throw new Error("User MRN is required");

      // Remove each space membership
      for (const spaceMrn of data.removedSpaces) {
        await removeSpaceMembership({
          variables: {
            input: {
              spaceMrn,
              userMrn,
            },
          },
          refetchQueries: [GetUserProfileDocument],
        });
      }

      const count = data.removedSpaces.length;
      enqueueSnackbar(
        `Successfully removed access to ${count} space${count !== 1 ? "s" : ""}`,
        {
          variant: "success",
        },
      );
      setIsEditUsersDialogOpen(false);
    } catch (error) {
      const errorMessage = getError(error);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };

  const handleEditOrgRole = () => {
    setIsEditOrgRoleDialogOpen(true);
  };

  const handleSaveOrgRole = async (roles: Array<{ mrn: string }>) => {
    try {
      if (!userMrn) throw new Error("User MRN is required");
      if (!org.mrn) throw new Error("Organization MRN is required");

      await setOrganizationMembership({
        variables: {
          input: {
            orgMrn: org.mrn,
            userMrn,
            roles,
          },
        },
        refetchQueries: [LoadOrganizationUsersDocument, GetUserProfileDocument],
      });

      enqueueSnackbar("User role updated successfully", {
        variant: "success",
      });
      setIsEditOrgRoleDialogOpen(false);
    } catch (error) {
      const errorMessage = getError(error);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };

  const handleCancelEditOrgRole = () => {
    setIsEditOrgRoleDialogOpen(false);
  };

  const handleOpenEditSpaceRole = (space: SpaceAccess) => {
    setEditSpaceRoleDialog({ open: true, space });
  };

  const handleCloseEditSpaceRole = () => {
    setEditSpaceRoleDialog({ open: false, space: null });
  };

  const handleSaveSpaceRole = async (
    spaceMrn: string,
    roles: Array<{ mrn: string }>,
  ) => {
    try {
      if (!userMrn) throw new Error("User MRN is required");

      await setSpaceMembership({
        variables: {
          input: {
            spaceMrn,
            userMrn,
            roles,
          },
        },
        refetchQueries: [GetUserProfileDocument],
      });

      enqueueSnackbar("Space role updated successfully", {
        variant: "success",
      });
      handleCloseEditSpaceRole();
    } catch (error) {
      const errorMessage = getError(error);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };

  const handleRemoveSpaceAccess = async (spaceMrn: string) => {
    try {
      if (!userMrn) throw new Error("User MRN is required");

      await removeSpaceMembership({
        variables: {
          input: {
            spaceMrn,
            userMrn,
          },
        },
        refetchQueries: [GetUserProfileDocument],
      });

      enqueueSnackbar("Space access removed successfully", {
        variant: "success",
      });
      handleCloseEditSpaceRole();
    } catch (error) {
      const errorMessage = getError(error);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };

  const handleSelectSpaceToAdd = (space: { mrn: string; name: string }) => {
    setAddSpaceDialog({ open: true, space });
  };

  const handleCloseAddSpaceDialog = () => {
    setAddSpaceDialog({ open: false, space: null });
  };

  const handleAddSpaceAccess = async (
    spaceMrn: string,
    roles: Array<{ mrn: string }>,
  ) => {
    try {
      if (!userMrn) throw new Error("User MRN is required");

      await setSpaceMembership({
        variables: {
          input: {
            spaceMrn,
            userMrn,
            roles,
          },
        },
        refetchQueries: [GetUserProfileDocument],
      });

      enqueueSnackbar("Space access added successfully", {
        variant: "success",
      });
      handleCloseAddSpaceDialog();
    } catch (error) {
      const errorMessage = getError(error);
      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    }
  };

  return {
    // Data
    user,
    loading,
    error,
    filteredSpaces,

    // Search
    spaceSearch,
    setSpaceSearch,

    // Spaces pagination
    spacesPaginationModel,
    onSpacesPaginationModelChange: handleSpacesPaginationModelChange,
    spacesRowCount,

    // Remove dialog
    isRemoveDialogOpen,
    handleRemoveUser,
    handleConfirmRemove,
    handleCancelRemove,
    isRemoveUserLoading,

    // Edit users dialog
    isEditUsersDialogOpen,
    handleEditUsers,
    handleSaveEditUsers,
    handleCancelEditUsers,

    // Edit organizational role dialog (nested)
    isEditOrgRoleDialogOpen,
    handleEditOrgRole,
    handleSaveOrgRole,
    handleCancelEditOrgRole,
    isEditRoleLoading,

    // Other handlers
    handleBackToUsers,
    handleRemoveSpaceAccess,

    // Edit space role dialog
    editSpaceRoleDialog,
    handleOpenEditSpaceRole,
    handleCloseEditSpaceRole,
    handleSaveSpaceRole,
    isSaveSpaceRoleLoading,
    isRemoveSpaceLoading,

    // Add space dialog
    addSpaceDialog,
    handleSelectSpaceToAdd,
    handleCloseAddSpaceDialog,
    handleAddSpaceAccess,
  };
}
